import React, {useEffect, useRef} from 'react';
import {Link, useLocation} from "react-router-dom";
import {useTranslation} from 'react-i18next';
import {agencySectionsFor, isAgencyPage, isSectionActive} from "../layout/v2/agencyPages";
import {useAlertRulesAccess} from "../../context/UserModulesContext";

/**
 * Las solapas de la agencia en la 2.1 (tanda «Agencia 2.1», D2, contrato §1).
 *
 * - Salen de `AGENCY_SECTIONS`, la MISMA lista que el desplegable de la agencia y en el mismo orden:
 *   el desplegable y las solapas no pueden ofrecer cosas distintas. Con el Centro de Alertas,
 *   «Alertas» lleva a «Mis reglas» del Centro (`agencySectionsFor`), igual que en el desplegable.
 * - La activa se marca por RUTA (`-active`, como `ClientTabs.tabClass`), no por posición: la
 *   aritmética de `-first`…`-last` del clásico se corre con cada pestaña condicional.
 * - Sin «Clientes» ni «Portal»: los dos ya están en el menú lateral.
 * - Son la 2.ª fila del topbar, como las de la ficha del cliente: `AgencyHeader` no se dibuja en la
 *   2.1, así que la fila queda primera en `.section-content` y la toma la regla de `_ui-v2.scss`.
 * - Fuera de una pantalla de la agencia (el uso del portal, que también monta `AgencyTabs`) no dibuja
 *   nada: ahí no hay ninguna solapa que marcar.
 * - En angosto la fila scrollea en su lugar (`_ui-v2.scss`), y arranca con la activa a la vista: a
 *   390, en Perfil de agencia se veían «Conexiones · Métricas…» y nada decía dónde se estaba.
 */
export function AgencyTabsV2() {
    const {t} = useTranslation();
    const {pathname} = useLocation();
    const listRef = useRef(null);
    const {enabled: alertsCenter} = useAlertRulesAccess();

    useEffect(() => {
        const list = listRef.current;
        if (!list || list.scrollWidth <= list.clientWidth) return;
        const active = list.querySelector('.tab-button.-active');
        if (!active) return;
        const item = active.getBoundingClientRect();
        const offset = item.left - list.getBoundingClientRect().left + list.scrollLeft;
        list.scrollLeft = Math.max(0, offset - (list.clientWidth - item.width) / 2);
    }, [pathname]);

    if (!isAgencyPage(pathname)) return null;

    return (
        <nav className="filters agv2-tabs" aria-label={t('agency_v2.frame.tabs_label')}>
            <ul className="tabs" ref={listRef}>
                {agencySectionsFor({alertsCenter}).map((section) => {
                    const active = isSectionActive(section, pathname);
                    return (
                        <li key={section.key}>
                            <Link className={'tab-button' + (active ? ' -active' : '')} to={section.to}
                                  aria-current={active ? 'page' : undefined}>
                                {t(section.labelKey)}
                            </Link>
                        </li>
                    );
                })}
            </ul>
        </nav>
    );
}
